/**
 * Загрузка общей статистики TikTok аккаунтов за период — в той же форме
 * AccountStatsRow, что и Instagram, чтобы рендерить одной таблицей.
 * Display API не даёт Охваты/Показы: используем просмотры видео как «Показы»,
 * Охваты/ER-охват остаются null. Сравнение с прошлым периодом пока не считаем.
 *
 * Кэшируется по периоду (как Instagram): возврат на страницу показывает
 * последние загруженные строки мгновенно, без повторного запроса.
 */

import { useEffect, useState } from 'react';

import {
  getTikTokAccounts,
  getTikTokStats,
  getTikTokVideosInRange,
  tiktokLabel,
} from '../../../shared/api/tiktok.api';
import { useTikTokGeneralStatsCache } from '../store/tiktokGeneralStatsCache';

import type { AccountStatsRow, ResolvedDateRange } from '../types/account-stats';

export function useTikTokGeneralStats(dateRange: ResolvedDateRange): {
  rows: AccountStatsRow[];
  isLoading: boolean;
} {
  const from = dateRange.from.getTime();
  const to = dateRange.to.getTime();
  const cacheKey = `${from}_${to}`;

  const [state, setState] = useState<{ rows: AccountStatsRow[]; isLoading: boolean }>(() => {
    const cached = useTikTokGeneralStatsCache.getState();
    if (cached.cacheKey === cacheKey) return { rows: cached.rows, isLoading: false };
    return { rows: [], isLoading: true };
  });

  useEffect(() => {
    let cancelled = false;

    // Same period already loaded — reuse the cache, don't re-fetch.
    const cached = useTikTokGeneralStatsCache.getState();
    if (cached.cacheKey === cacheKey) {
      setState({ rows: cached.rows, isLoading: false });
      return;
    }

    setState({ rows: [], isLoading: true });

    (async () => {
      let accs: { openId: string; displayName: string; username: string }[] = [];
      try {
        accs = await getTikTokAccounts();
      } catch {
        if (!cancelled) setState({ rows: [], isLoading: false });
        return;
      }
      if (cancelled) return;
      if (accs.length === 0) {
        setState({ rows: [], isLoading: false });
        useTikTokGeneralStatsCache.getState().setCache(cacheKey, []);
        return;
      }

      const seeded: AccountStatsRow[] = accs.map((a) => ({
        accountId: `tt:${a.openId}`,
        platform: 'tiktok',
        username: tiktokLabel(a),
        name: a.displayName,
        avatarUrl: null,
        tags: [],
        followers: null,
        impressions: null,
        reach: null,
        interactions: null,
        er: null,
        erByReach: null,
        followersDelta: null,
        impressionsDelta: null,
        reachDelta: null,
        interactionsDelta: null,
        erDelta: null,
        erByReachDelta: null,
        isLoading: true,
        error: null,
      }));
      setState({ rows: seeded, isLoading: false });

      const byId = new Map(seeded.map((r) => [r.accountId, r]));
      for (const a of accs) {
        try {
          const [stats, vids] = await Promise.all([
            getTikTokStats(a.openId),
            getTikTokVideosInRange(a.openId, from, to),
          ]);
          const impressions = vids.reduce((s, v) => s + v.viewCount, 0);
          const interactions = vids.reduce((s, v) => s + v.likeCount + v.commentCount + v.shareCount, 0);
          const er = impressions > 0 ? (interactions / impressions) * 100 : null;
          byId.set(`tt:${a.openId}`, {
            ...byId.get(`tt:${a.openId}`)!,
            followers: stats.followerCount,
            impressions,
            interactions,
            er,
            isLoading: false,
          });
        } catch (err) {
          byId.set(`tt:${a.openId}`, {
            ...byId.get(`tt:${a.openId}`)!,
            isLoading: false,
            error: (err as Error).message,
          });
        }
        if (cancelled) return;
        setState({ rows: [...byId.values()], isLoading: false });
      }
      // Persist the fully-loaded rows so a return visit is instant.
      useTikTokGeneralStatsCache.getState().setCache(cacheKey, [...byId.values()]);
    })();

    return () => { cancelled = true; };
  }, [cacheKey, from, to]);

  return state;
}
